import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { DatabaseMetaInfo } from '../../../../types/electron';

/**
 * Result of inspecting a column for JSON-ness.
 *
 * Declared takes priority: any column with a `metaInfo.jsonColumns` row is
 * `'declared-json'` regardless of underlying type. Use `isNativeJsonColumn`
 * separately when you need the engine-type distinction (e.g. SQL emit
 * needs the cast-wrap path only for declared-on-text columns, not for
 * declared native JSON columns).
 *
 * - `declared-json` — the user has declared this column as JSON via the
 *   MetaInfo manager / filter-chip confirmation; it lives in
 *   `metaInfo.jsonColumns`. May or may not be a native JSON column.
 * - `native-json` — the column's engine type is a real JSON type
 *   (PG `json`/`jsonb`, MySQL `json`, MSSQL 2022+ `json`, Oracle 21c+ `json`)
 *   and has no declaration row yet.
 * - `json-eligible` — the column is a long-text type where a user *could*
 *   declare JSON but hasn't yet. Drives the "Declare as JSON?" affordance.
 * - `not-json` — none of the above.
 */
export type JsonColumnCategory =
  | 'native-json'
  | 'declared-json'
  | 'json-eligible'
  | 'not-json';

/**
 * Where the `json` operator should appear in the filter operator dropdown
 * for a given column.
 *
 * - `default` — auto-selected, top of the list (native + declared columns).
 * - `first` — top of the list but not auto-selected; the column's name ends
 *   in `json` (e.g. `payload_json`), strongly hinting JSON intent.
 * - `last` — bottom of the list (eligible long-text without a name hint).
 * - `absent` — operator not offered.
 */
export type JsonOperatorPlacement = 'default' | 'first' | 'last' | 'absent';

/**
 * Threshold for varchar/nvarchar/varchar2/nvarchar2 columns to be treated
 * as long-text, and therefore JSON-eligible. Columns shorter than this
 * almost always hold short labels (status strings, codes, slugs) — exposing
 * a `json` operator on them would just clutter the dropdown.
 */
export const JSON_ELIGIBLE_VARCHAR_THRESHOLD = 256;

export type JsonColumnInput = {
  engine: DatabaseEngineId;
  schema: string;
  table: string;
  name: string;
  /** Engine-specific column data type string, e.g. `nvarchar(max)`, `jsonb`, `varchar2(4000)`. */
  dataType: string;
};

type ParsedType = {
  /** Lowercased base type with no parens, e.g. `nvarchar`, `jsonb`, `varchar2`. */
  base: string;
  /** Numeric length inside `(N)` if specified and finite, otherwise null. */
  length: number | null;
  /** True for MSSQL `(max)` length. */
  isMax: boolean;
};

function parseDataType(raw: string): ParsedType {
  // Canonicalize PG multi-word aliases before the regex. The regex tail
  // `(?:\s.*)?` happily swallows the second word as a "modifier", so a
  // post-regex `if (base === 'character varying')` rewrite would never
  // fire — it sees just `character`. Doing the rewrite up front keeps the
  // single-word base assumption inside the regex.
  // `raw` is typed `string`, but callers feed it `column.dataType` which is
  // `string | undefined` at runtime (engine metadata gaps, session-restored
  // tabs whose columns predate a dataType field, etc.). A missing type must
  // degrade to the same neutral result an unparseable type yields — never
  // throw, since this runs per-column on every grid render and an
  // uncaught throw trips the app-wide ErrorBoundary.
  const lowered = (typeof raw === 'string' ? raw : '')
    .trim()
    .toLowerCase()
    .replace(/^character\s+varying\b/u, 'varchar')
    .replace(/^character(?!\s+varying)\b/u, 'char');
  const match = lowered.match(/^([a-z0-9_ ]+?)\s*(?:\(\s*([^)]*?)\s*\))?(?:\s.*)?$/u);
  if (!match) return { base: lowered, length: null, isMax: false };
  let base = (match[1] ?? '').trim().replace(/\s+/gu, ' ');
  if (base === 'bpchar') base = 'char';
  const lengthRaw = (match[2] ?? '').trim();
  const isMax = lengthRaw === 'max';
  const length = !isMax && /^\d+$/u.test(lengthRaw) ? parseInt(lengthRaw, 10) : null;
  return { base, length, isMax };
}

/**
 * True when a column's engine type is a real JSON type. Stays valid even
 * after the column is declared — `categorizeJsonColumn` no longer surfaces
 * native-ness once a declaration row exists (Option B), so call this when
 * you need to branch on "the underlying column is engine-typed JSON" (SQL
 * emit, dialog/badge copy, etc.). See `[[reference-json-glyphs-option-b]]`.
 */
export function isNativeJsonColumn(column: JsonColumnInput): boolean {
  return isNativeJson(column.engine, parseDataType(column.dataType));
}

function isNativeJson(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  switch (engine) {
    case 'pgsql':
      return parsed.base === 'json' || parsed.base === 'jsonb';
    case 'mysql':
      return parsed.base === 'json';
    case 'mssql':
      return parsed.base === 'json';
    case 'oracle':
      return parsed.base === 'json';
    case 'sqlite':
      return false;
    case 'kusto':
      // Kusto's `dynamic` CSL type IS JSON (arbitrary nested objects/arrays).
      // The Table Explorer metadata carries the raw CSL type (`getschema`'s
      // `ColumnType`), so we key on `dynamic` — NOT the `json` alias
      // `mapKustoColumnType` produces for query-result columns.
      return parsed.base === 'dynamic';
    case 'azurestorage':
      // Table storage entities carry tagged EDM values, never a JSON column type.
      return false;
    case 'mock':
      return parsed.base === 'json' || parsed.base === 'jsonb';
  }
}

function isJsonEligibleTextType(engine: DatabaseEngineId, parsed: ParsedType): boolean {
  switch (engine) {
    case 'mssql': {
      if (parsed.base === 'text' || parsed.base === 'ntext') return true;
      if (parsed.base === 'nvarchar' || parsed.base === 'varchar') {
        if (parsed.isMax) return true;
        if (parsed.length === null) return false;
        return parsed.length >= JSON_ELIGIBLE_VARCHAR_THRESHOLD;
      }
      return false;
    }
    case 'oracle': {
      if (parsed.base === 'clob' || parsed.base === 'nclob') return true;
      if (parsed.base === 'varchar2' || parsed.base === 'nvarchar2') {
        if (parsed.length === null) return false;
        return parsed.length >= JSON_ELIGIBLE_VARCHAR_THRESHOLD;
      }
      return false;
    }
    case 'sqlite': {
      // SQLite has TEXT affinity for any column whose declared type contains
      // 'CHAR', 'CLOB', or 'TEXT'. We also accept declared types containing
      // 'JSON' — SQLite has no native JSON type, but users routinely write
      // `data JSON` in CREATE TABLE to signal intent, and we should honour it.
      return (
        parsed.base.includes('char') ||
        parsed.base.includes('clob') ||
        parsed.base.includes('text') ||
        parsed.base.includes('json')
      );
    }
    case 'pgsql': {
      // `text` is unbounded, always eligible. `varchar` / `character varying`
      // without a length is also unbounded; with `(N)` we apply the same
      // ≥256 threshold as MSSQL/Oracle so short label columns don't get a
      // JSON operator. PG declare-on-text requires server version 16+ at
      // SQL emit time (uses `IS JSON` / `(col)::jsonb`); the categorization
      // is version-agnostic so the operator still surfaces in the dropdown
      // on older servers and the dialog can explain the version requirement.
      if (parsed.base === 'text') return true;
      if (parsed.base === 'varchar') {
        if (parsed.length === null) return true;
        return parsed.length >= JSON_ELIGIBLE_VARCHAR_THRESHOLD;
      }
      return false;
    }
    case 'mysql': {
      // MySQL has `tinytext` (≤255), `text`, `mediumtext`, `longtext`. The
      // first is below the threshold and rarely holds JSON; the others are
      // always eligible. `varchar(N)` follows the same ≥256 rule as MSSQL.
      if (parsed.base === 'text' || parsed.base === 'mediumtext' || parsed.base === 'longtext') {
        return true;
      }
      if (parsed.base === 'varchar') {
        if (parsed.length === null) return false;
        return parsed.length >= JSON_ELIGIBLE_VARCHAR_THRESHOLD;
      }
      return false;
    }
    case 'kusto':
      return false;
    case 'azurestorage':
      return parsed.base === 'edm.string';
    case 'mock':
      return false;
  }
}

function isDeclared(column: JsonColumnInput, metaInfo: DatabaseMetaInfo | null | undefined): boolean {
  if (!metaInfo) return false;
  return metaInfo.jsonColumns.some(
    (decl) =>
      decl.schema === column.schema &&
      decl.table === column.table &&
      decl.column === column.name,
  );
}

/**
 * Categorize a column by its JSON-ness. `native-json` and `declared-json`
 * columns get the rich JSON UI; `json-eligible` columns get the "declare as
 * JSON?" affordance via the filter-chip operator dropdown. `not-json`
 * columns get the standard non-JSON operator set.
 */
export function categorizeJsonColumn(
  column: JsonColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): JsonColumnCategory {
  const parsed = parseDataType(column.dataType);
  // Declared takes priority — a declaration row signals user-supplied
  // metadata (path descriptions, loose-FK targets) regardless of the
  // underlying column type. Native vs. text is preserved through the
  // separate `isNativeJsonColumn` helper for places that branch on it
  // (SQL emit, "Native JSON column" badge copy).
  if (isDeclared(column, metaInfo)) return 'declared-json';
  if (isNativeJson(column.engine, parsed)) return 'native-json';
  if (isJsonEligibleTextType(column.engine, parsed)) return 'json-eligible';
  return 'not-json';
}

function nameSuggestsJson(name: string): boolean {
  return /json$/iu.test(name.trim());
}

/**
 * Resolve where the `json` operator should be placed in the filter operator
 * dropdown for a column. See `JsonOperatorPlacement`.
 */
export function inferJsonOperatorPlacement(
  column: JsonColumnInput,
  metaInfo: DatabaseMetaInfo | null | undefined,
): JsonOperatorPlacement {
  const category = categorizeJsonColumn(column, metaInfo);
  switch (category) {
    case 'native-json':
    case 'declared-json':
      return 'default';
    case 'json-eligible':
      return nameSuggestsJson(column.name) ? 'first' : 'last';
    case 'not-json':
      return 'absent';
  }
}
